import {
  Enums,
  PanTool,
  StackScrollTool,
  WindowLevelTool,
  ZoomTool,
} from '@cornerstonejs/tools';
import { OHIF } from '@gleamer/types';

type SavedToolConfigurations = {
  bindings: OHIF.ToolConfigBinding[];
  toolName: string;
}[];

// Versioned deliberately. The v1 snapshot has no version field and was written
// whole on every Preferences save, so it pins whatever the defaults were that
// day. Users who saved before StackScrollMouseWheelTool was retired kept
// `StackScrollTool: bindings: []`; once that tool was removed from the tool
// groups, mouse-wheel slice scrolling stopped working for them with no error
// and no prompt. Reconciling cannot fix it — a stale entry is indistinguishable
// from a deliberate unbinding — so bump the key once and let the corrected
// defaults apply. The cost is that existing mouse customisations are reset
// once, which is visible and re-doable; dead wheel scrolling is neither.
export function getMouseBindingsConfigsKey() {
  return `mouse-bindings-config-v2`;
}

export function getDefaultMouseBindings(): SavedToolConfigurations {
  return [
    {
      toolName: ZoomTool.toolName,
      bindings: [{ mouseButton: Enums.MouseBindings.Auxiliary }],
    },
    {
      toolName: WindowLevelTool.toolName,
      bindings: [
        {
          mouseButton: Enums.MouseBindings.Fourth_Button,
        },
        {
          mouseButton: Enums.MouseBindings.Primary,
          modifierKey: Enums.KeyboardBindings.Shift,
        },
      ],
    },
    {
      toolName: PanTool.toolName,
      bindings: [
        {
          mouseButton: Enums.MouseBindings.Primary,
        },
      ],
    },
    {
      // CS3D 2.0 removed StackScrollMouseWheelTool; StackScrollTool now handles
      // mouse-wheel scrolling itself when bound to the Wheel mouse button.
      toolName: StackScrollTool.toolName,
      bindings: [{ mouseButton: Enums.MouseBindings.Wheel }],
    },
    {
      toolName: 'Annotation',
      bindings: [
        {
          mouseButton: Enums.MouseBindings.Secondary,
        },
      ],
    },
  ];
}

export function getMouseBindingsConfigs(): SavedToolConfigurations {
  const configKey = getMouseBindingsConfigsKey();
  const config = localStorage.getItem(configKey);
  const defaults = getDefaultMouseBindings();

  if (!config) {
    return defaults;
  }

  let saved: SavedToolConfigurations;
  try {
    saved = JSON.parse(config);
  } catch (e) {
    console.warn('Could not parse the saved mouse bindings; using defaults', e);
    return defaults;
  }

  // Reconcile against the defaults rather than returning the stored snapshot
  // verbatim. The snapshot is written whole whenever the user saves
  // Preferences, and it has no version field, so it pins whatever the defaults
  // were on the day they saved. That silently breaks users on every default
  // change: when StackScrollMouseWheelTool was removed and wheel scrolling
  // moved onto StackScrollTool, anyone with a saved config kept
  // `StackScrollTool: bindings: []` and lost mouse-wheel slice scrolling
  // entirely — no error, no prompt.
  //
  // Start from the current defaults so newly-introduced tools are always
  // present, and overlay only the saved entries for tools that still exist, so
  // a genuine user customisation still wins.
  return defaults.map(defaultConfig => {
    const savedConfig = saved.find(
      ({ toolName }) => toolName === defaultConfig.toolName
    );
    return savedConfig ?? defaultConfig;
  });
}

export function getBindingsForTool(toolName: string) {
  const savedConfigs = getMouseBindingsConfigs();
  const toolConfig = savedConfigs.find(
    ({ toolName: name }) => name === toolName
  );
  return toolConfig ? toolConfig.bindings : [];
}
